import { KikUserZodSchemas as ZodSchemas } from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import classNames from "classnames";
import { useTranslations } from "next-intl";
import { CSSProperties, DetailedHTMLProps, InputHTMLAttributes, useEffect, useId, useRef, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { DateTime } from "ts-luxon";

import Icon from "~/components/common/icon";
import { useBreakpoint } from "~/contexts/breakpoint";
import IntlUtils from "~/utils/intl-utils";

import styles from "./input-field-date.module.scss";

type InputDateProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  label: string;
  isOptional?: boolean;
  displayDateFormat?: string;
  minimumDate?: DateTime | Date;
  maximumDate?: DateTime | Date;
  error?: string;
  onClearErrors?: () => void;
  control?: Control<any>;
  name: string;
};

export default function InputFieldDateMui({
  label,
  isOptional,
  displayDateFormat = IntlUtils.defaultDateFormat,
  minimumDate,
  maximumDate,
  error,
  onClearErrors,
  control,
  className,
  name,
  ...rest
}: InputDateProps) {
  const t = useTranslations();
  const id = useId();
  const breakpoint = useBreakpoint();
  const [isInputFocused, setIsInputFocused] = useState(false);

  //the width of the label that will be used to create a transparent label is kept track of
  const refLabel = useRef<HTMLLabelElement>(null);
  const [widthLabel, setWidthLabel] = useState<number | undefined>();
  const minDate = (minimumDate instanceof Date ? DateTime.fromJSDate(minimumDate) : minimumDate) as DateTime;
  const maxDate = !maximumDate
    ? DateTime.fromJSDate(new Date())
    : maximumDate instanceof Date
    ? DateTime.fromJSDate(maximumDate)
    : maximumDate;
  const styleLabelLine = {
    "--label-line-width": `calc(100% - ${widthLabel}px - var(--label-external-padding-horizontal))`,
  } as CSSProperties;

  useEffect(() => {
    setWidthLabel(refLabel.current?.offsetWidth);
  }, [isOptional]);

  return (
    <div
      className={classNames(
        styles.container,
        error && styles.containerWithErrors,
        isInputFocused && styles.containerInFocus,
        className
      )}
    >
      <div className={styles.wrapperLabel}>
        <label
          ref={refLabel}
          htmlFor={id}
          onMouseDown={(e) => e.preventDefault()}
          style={styleLabelLine}
          className={styles.label}
        >
          {label}
          {isOptional ? (
            ""
          ) : (
            <span className={styles.mandatory} aria-hidden>
              {t("generic.form.mandatory_short")}
            </span>
          )}
        </label>
      </div>
      <div className={styles.wrapperInput}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, onBlur, value, ref } }) => {
            switch (breakpoint) {
              case "mobile":
                return (
                  <input
                    type="date"
                    min={minDate?.toFormat(IntlUtils.defaultDatepickerFormat)}
                    max={maxDate.toFormat(IntlUtils.defaultDatepickerFormat)}
                    className={classNames(
                      styles.inputDate,
                      styles.inpuDateSystem,
                      rest.disabled ? styles.inputDateUneditable : null
                    )}
                    {...rest}
                    value={value ? DateTime.fromISO(value).toFormat(IntlUtils.defaultDatepickerFormat) : ""}
                    onChange={(evt) => {
                      onChange(
                        evt?.target?.value
                          ? DateTime.fromISO(evt.target.value).toFormat(IntlUtils.defaultDatepickerFormat)
                          : ""
                      );
                    }}
                    ref={ref}
                    id={id}
                    onClick={() => onClearErrors?.()}
                    onFocus={() => {
                      onClearErrors?.();
                      setIsInputFocused?.(true);
                    }}
                    onBlur={() => {
                      onBlur();
                      setIsInputFocused?.(false);
                    }}
                  />
                );
              case "desktop":
              default:
                return (
                  <DesktopDatePicker
                    name={name}
                    value={value ? DateTime.fromISO(value) : null}
                    onChange={(newValue) => {
                      if (newValue && newValue.isValid) {
                        onChange(newValue.toISO());
                      } else {
                        onChange(undefined);
                      }
                    }}
                    minDate={minDate}
                    maxDate={maxDate}
                    slotProps={{
                      textField: {
                        onClick: () => onClearErrors?.(),
                        onFocus: () => {
                          onClearErrors?.();
                          setIsInputFocused?.(true);
                        },
                        onBlur: () => {
                          onBlur();
                          setIsInputFocused?.(false);
                        },
                        fullWidth: true,
                        classes: {
                          root: classNames(styles.inputDateNew, rest.disabled ? styles.inputDateUneditable : null),
                        },
                      },
                    }}
                    onOpen={() => onClearErrors?.()}
                  />
                );
            }
          }}
        />
        {breakpoint == "mobile" &&
          (rest.disabled ? (
            <Icon name="uneditable" width={16} height={16} className={styles.iconUneditable} />
          ) : (
            <Icon name="appointment" width={16} height={16} className={styles.icon} />
          ))}
      </div>
      {error && (
        <p id={`${id}-error`} className={styles.errorMessage}>
          {error}
        </p>
      )}
    </div>
  );
}
